<div class="page-shell current-employee-page-wrapper">
  <mat-toolbar class="header">
    <span>{{ employee?.email || ("menu.myAccount" | translate) }}</span>
    <div class="header-buttons">
      <ng-container *ngIf="!isLoading && canForceSyncEmployee(employee?.dataSource)">
        <span class="data-source-type">{{ getEmployeeDataSourceLabel(employee?.dataSource) | translate }}</span>
        <button
          mat-stroked-button
          type="button"
          (click)="forceSyncEmployee()"
          [disabled]="isSyncing || isLoading"
        >
          <mat-spinner
            diameter="20"
            *ngIf="isSyncing"
            class="dc-btn-spinner"
          ></mat-spinner>
          <mat-icon *ngIf="!isSyncing">sync</mat-icon>
          {{ "employee.forceSync" | translate }}
        </button>
      </ng-container>
      <button
        *ngIf="!isLoading && (employee?.signature || employee?.signatureInternal)"
        type="button"
        mat-raised-button
        color="primary"
        (click)="saveAndReinstall()"
        [disabled]="!form.dirty || isLoading || saving"
      >
        <mat-spinner
          diameter="20"
          *ngIf="saving"
          class="dc-btn-spinner"
        ></mat-spinner>
        <mat-icon *ngIf="!saving">install_desktop</mat-icon>
        <span>{{
          saving ? ("saving" | translate) : ("saveAndReinstall" | translate)
        }}</span>
      </button>
      <button
        mat-raised-button
        color="primary"
        type="button"
        (click)="onSubmit()"
        [disabled]="!form.dirty || isLoading || saving || !employee"
      >
        <mat-spinner
          diameter="20"
          *ngIf="saving"
          class="dc-btn-spinner"
        ></mat-spinner>
        <mat-icon *ngIf="!saving">save</mat-icon>
        <span>{{
          saving ? ("saving" | translate) : ("save" | translate)
        }}</span>
      </button>
    </div>
  </mat-toolbar>

  <div
    class="current-employee-wrapper"
    *ngIf="isLoading || authService.hasEmployee; else noEmployee"
  >
    <div class="content-layout">
      <div class="main-content">
        <div class="form-section" *ngIf="employee?.signature || employee?.signatureInternal">
          <h3 class="section-title">{{ "employee.yourCurrentSignatureDescription" | translate }}</h3>
          <div class="signature-content-wrapper">
            <div *ngIf="employee?.signature && employee?.signatureInternal" class="signature-toggle">
              <mat-button-toggle-group [(ngModel)]="previewType" (ngModelChange)="updateParsedSignature()" aria-label="Signature preview type" color="primary">
                <mat-button-toggle value="external">{{ 'externalSignature' | translate }}</mat-button-toggle>
                <mat-button-toggle value="internal">{{ 'internalSignature' | translate }}</mat-button-toggle>
              </mat-button-toggle-group>
            </div>
            <div class="signature-preview">
              <div class="signature-wrapper" #previewContainer>
                <div [innerHTML]="parsedSignature | safeHtml" class="signature-content" appScaleContent [appScaleContent]="previewContainer"></div>
              </div>
            </div>
            <div class="signature-actions">
              <button mat-stroked-button (click)="copySignaturePlainToClipboard()">
                <mat-icon>content_copy</mat-icon>
                <span>{{ "employee.copySignaturePlain" | translate }}</span>
              </button>
              <button mat-stroked-button (click)="copySignatureToClipboard()">
                <mat-icon>content_copy</mat-icon>
                <span>{{ "employee.copySignature" | translate }}</span>
              </button>
              <button mat-stroked-button (click)="downloadSignatureOutlook()">
                <mat-icon>download</mat-icon>
                <span>{{ "employee.downloadSignatureOutlook" | translate }}</span>
              </button>
            </div>
          </div>
        </div>

        <form [formGroup]="form" class="form" (ngSubmit)="onSubmit()">
          <div class="form-section">
            <h3 class="section-title">{{ "employee.yourCurrentSignatureSettingsDescription" | translate }}</h3>
            <div *ngIf="settings?.bannerDisablingByUser" class="banner-disabling">
              <mat-checkbox formControlName="bannerDisabled">
                {{ "employee.bannerDisabled" | translate }}
              </mat-checkbox>
            </div>

            <div class="fields-wrapper">
              <mat-form-field appearance="outline">
                <mat-label>{{ profileFieldLabel("email") }}</mat-label>
                <input matInput formControlName="email" type="text" />
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>{{ profileFieldLabel("givenName") }}</mat-label>
                <input matInput formControlName="givenName" type="text" [errorStateMatcher]="errorStateMatcher" />
                <mat-error *ngIf="form.hasError('required', ['givenName'])">
                  {{ "fieldRequired" | translate }}
                </mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>{{ profileFieldLabel("familyName") }}</mat-label>
                <input matInput formControlName="familyName" type="text" [errorStateMatcher]="errorStateMatcher" />
                <mat-error *ngIf="form.hasError('required', ['familyName'])">
                  {{ "fieldRequired" | translate }}
                </mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>{{ profileFieldLabel("userAbout") }}</mat-label>
                <textarea matInput formControlName="userAbout" rows="3" [errorStateMatcher]="errorStateMatcher"></textarea>
                <mat-error *ngIf="form.hasError('required', ['userAbout'])">
                  {{ "fieldRequired" | translate }}
                </mat-error>
              </mat-form-field>

              <div [formGroup]="dynamicVariables" class="dynamic-fields">
                <mat-form-field appearance="outline" *ngFor="let employSetting of dynamicEmployeeSettings">
                  <mat-label>{{
                    employSetting.label ||
                    ("employee.signatureVariables." + employSetting.name
                    | translate)
                    }}</mat-label>
                  <input matInput [formControlName]="employSetting.name" type="text" [errorStateMatcher]="errorStateMatcher" />
                  <mat-error *ngIf="
                      dynamicVariables.hasError('required', [employSetting.name])
                    ">
                    {{ "fieldRequired" | translate }}
                  </mat-error>
                  <mat-error *ngIf="
                      dynamicVariables.hasError('phoneNumberInvalid', [
                        employSetting.name
                      ])
                    ">
                    {{ "phoneNumberInvalid" | translate }}
                  </mat-error>
                </mat-form-field>
              </div>
            </div>
          </div>

          <app-employee-branch-section
            [branchId]="branchId"
            [employeeBranchVariables]="branchVariables"
            [errorStateMatcher]="errorStateMatcher"
            (branchChange)="onBranchChange($event)">
          </app-employee-branch-section>

          <app-employee-vcard
            [parentForm]="form"
            [entries]="vcardEntries"
            [loadVersion]="vcardLoadVersion"
            [errorStateMatcher]="errorStateMatcher"
            (changed)="form.markAsDirty()"
          ></app-employee-vcard>

          <div class="form-section" *ngIf="showNameProfile">
            <h3 class="section-title">
              {{ "employee.nameProfile" | translate }}
              <span class="dbc1-inactive-tag" *ngIf="nameProfileInactive">{{ "menu.inactive" | translate }}</span>
            </h3>
            <div class="fields-wrapper">
              <mat-form-field appearance="outline">
                <mat-label>{{ "employee.gender" | translate }}</mat-label>
                <mat-select formControlName="gender">
                  <mat-option [value]="null">{{ "employee.genderUnspecified" | translate }}</mat-option>
                  <mat-option *ngFor="let option of genderOptions" [value]="option.value">
                    {{ option.labelKey | translate }}
                  </mat-option>
                </mat-select>
              </mat-form-field>

              <app-country-select
                formControlName="countryOfOrigin"
                [label]="'employee.countryOfOrigin' | translate"
                [errorStateMatcher]="errorStateMatcher"
              ></app-country-select>
            </div>
          </div>

          <div class="form-section" *ngIf="settings?.linksEnabled">
            <h3 class="section-title">{{ "employee.links.label" | translate }}</h3>
            <div formArrayName="customLinks" (change)="form.markAsDirty()" class="links-list">
              <div *ngFor="let link of customLinks.controls; let i = index" class="links-form-row" [formGroup]="$any(link)">
                <mat-form-field appearance="outline">
                  <mat-label>{{ "employee.links.text" | translate }}</mat-label>
                  <input matInput formControlName="text" type="text" [errorStateMatcher]="errorStateMatcher" />
                  <mat-error *ngIf="link.hasError('required', ['text'])">
                    {{ "fieldRequired" | translate }}
                  </mat-error>
                </mat-form-field>

                <mat-form-field appearance="outline">
                  <mat-label>{{ "employee.links.url" | translate }}</mat-label>
                  <input matInput formControlName="url" type="text" [errorStateMatcher]="errorStateMatcher" />
                  <mat-error *ngIf="link.hasError('required', ['url'])">
                    {{ "fieldRequired" | translate }}
                  </mat-error>
                  <mat-error *ngIf="link.hasError('urlInvalid', ['url'])">
                    {{ "urlInvalid" | translate }}
                  </mat-error>
                </mat-form-field>

                <div class="delete-wrapper">
                  <button mat-icon-button (click)="removeLink(i); form.markAsDirty()">
                    <mat-icon class="delete-btn">delete_outline</mat-icon>
                  </button>
                </div>
              </div>
            </div>

            <div class="add-link-action">
              <button mat-stroked-button (click)="addLink()">
                <mat-icon>add</mat-icon>
                <span>{{ 'add' | translate }}</span>
              </button>
            </div>
          </div>

          <div class="form-section" *ngIf="settings?.handwrittenEnabled">
            <h3 class="section-title">{{ 'settings.handwrittenEnabled' | translate }}</h3>
            <app-handwritten-signature (handwrittenChange)="form.markAsDirty()" [form]="form"></app-handwritten-signature>
          </div>
        </form>
      </div>

      <div class="side-content" *ngIf="settings?.avatarEnabled">
        <div class="form-section">
          <h3 class="section-title">{{ 'employee.profilePicture' | translate }}</h3>
          <div class="profile-picture-wrapper">
            <app-avatar
              [avatarUrl]="displayThumbnailPhotoUrl"
              [avatarUploadUrl]="'/user/avatar'"
              [localAvatar]="employee?.localAvatar"
              [size]="120"
              [employeeId]="employee?.employeeId"
              (avatarChanged)="onAvatarChanged()">
            </app-avatar>
            <button
              *ngIf="employee?.thumbnailPhotoUrl && !pendingRemoveAvatar"
              type="button"
              class="remove-photo-btn"
              [matTooltip]="'employee.avatar.removePhoto' | translate"
              [attr.aria-label]="'employee.avatar.removePhoto' | translate"
              (click)="removeAvatar()">
              <mat-icon>close</mat-icon>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <ng-template #noEmployee>
    <div class="no-employee-message">
      <mat-card>
        <mat-card-content>
          <div class="message-content">
            <mat-icon class="message-icon">person_off</mat-icon>
            <h2>{{ "employee.noEmployeeTitle" | translate }}</h2>
            <p>{{ "employee.noEmployeeMessage" | translate }}</p>
          </div>
        </mat-card-content>
      </mat-card>
    </div>
  </ng-template>

  <div class="dbc1-loading dbc1-loading--overlay" *ngIf="isLoading">
    <app-loading></app-loading>
  </div>
</div>
